import { useRouter } from 'expo-router';
import React, { useCallback } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { REDESIGN_COLORS } from '../../styles/colors';
import { useTier } from '../../context/TierContext';

// D-018 visibility + label matrix. `inGrace` is `pro_expires_at > now()`.
// `superscript` renders only for trial_active (per design 2026-04-20; grandfather superscript dropped).
//
// `action` is set ONLY for states where the user has an existing Paddle
// subscription that can be resumed/reactivated through billing-action. For
// those, the press handler deeplinks to BillingModal (on /profile) with the
// matching billing-action. For pure upgrade states (free, expired, grace-
// exhausted cancelled, etc.), `action` stays unset and we open the upgrade
// modal / checkout flow the way we always have.
const resolveHeaderCTA = (subscriptionStatus, proExpiresAt, hasProAccess) => {
  const now = new Date();
  const proExpiry = proExpiresAt ? new Date(proExpiresAt) : null;
  const inGrace = proExpiry && proExpiry > now;

  switch (subscriptionStatus) {
    case 'active':
      return null;
    // Step 8: a carded user (card-on-file, pre day-7 charge) already has full
    // Pro and a recurring sub funding it. No upgrade CTA: the Pro badge alone
    // signals their state, mirroring the 'active' branch.
    case 'trial_carded':
      return null;
    case 'grandfather_active':
      return { label: 'Upgrade', source: 'grandfather_header_cta' };
    case 'trial_active':
      return { label: 'Upgrade', source: 'trial_header_cta', superscript: 'Pro' };
    case 'cancelled':
      return inGrace
        ? { label: 'Reactivate', source: 'cancelled_grace_cta', action: 'reactivate' }
        : { label: 'Reactivate', source: 'free_header_cta' };
    case 'past_due':
      return inGrace ? null : { label: 'Reactivate', source: 'free_header_cta' };
    case 'paused':
      return inGrace
        ? { label: 'Resume', source: 'paused_grace_cta', action: 'resume' }
        : { label: 'Resume', source: 'paused_header_cta', action: 'resume' };
    case 'trial_expired':
    case 'grandfather_expired':
    case 'none':
      return { label: 'Upgrade', source: 'free_header_cta' };
    default:
      return hasProAccess ? null : { label: 'Upgrade', source: 'free_header_cta' };
  }
};

const HeaderCTA = ({ variant = 'solid' }) => {
  const router = useRouter();
  const { subscriptionStatus, proExpiresAt, hasProAccess, loading, triggerUpgradeModal } = useTier();

  const ctaSpec = loading ? null : resolveHeaderCTA(subscriptionStatus, proExpiresAt, hasProAccess);

  const handlePress = useCallback(() => {
    if (!ctaSpec) return;
    // For paused/cancelled-in-grace, route to BillingModal on /profile via
    // the deeplink mechanism so billing-action runs Paddle's resume /
    // reactivate flow. Previously every CTA opened triggerUpgradeModal,
    // which would start a fresh Paddle checkout and leave the original
    // paused/cancelled subscription unresolved.
    if (ctaSpec.action === 'resume' || ctaSpec.action === 'reactivate') {
      router.push(
        `/profile?billing=open&source=${encodeURIComponent(ctaSpec.source)}&action=${encodeURIComponent(ctaSpec.action)}`,
      );
      return;
    }
    triggerUpgradeModal({ source: ctaSpec.source });
  }, [ctaSpec, router, triggerUpgradeModal]);

  if (!ctaSpec) return null;

  const a11y = ctaSpec.superscript ? `${ctaSpec.label}, ${ctaSpec.superscript}` : ctaSpec.label;

  return (
    <Pressable
      style={[
        styles.pill,
        variant === 'outline' && styles.pillOutline,
        ctaSpec.superscript && styles.pillWithSuperscript,
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={a11y}
      testID="header-cta"
    >
      <Text style={[styles.label, variant === 'outline' && styles.labelOutline]}>{ctaSpec.label}</Text>
      {ctaSpec.superscript ? (
        <Text
          style={[styles.superscript, variant === 'outline' && styles.superscriptOutline]}
          accessibilityElementsHidden
          importantForAccessibility="no"
        >
          {ctaSpec.superscript}
        </Text>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  pill: {
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 6,
    position: 'relative',
  },
  // When a superscript is present, grow horizontal padding symmetrically so the label
  // stays centered and the chip floats off the top-right corner.
  pillWithSuperscript: {
    paddingHorizontal: 18,
  },
  pillOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: REDESIGN_COLORS.ctaBlue,
  },
  label: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    lineHeight: 16,
    color: '#FFFFFF',
  },
  labelOutline: {
    color: REDESIGN_COLORS.ctaBlue,
  },
  superscript: {
    position: 'absolute',
    top: -9,
    right: -14,
    fontFamily: 'Satoshi-Bold',
    fontSize: 10,
    lineHeight: 14,
    letterSpacing: 0.3,
    color: '#FFFFFF',
    backgroundColor: '#0E264D',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#FFFFFF',
    overflow: 'hidden',
    // Subtle lift so the chip reads as hovering above the pill
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3,
    elevation: 3,
  },
  superscriptOutline: {
    color: '#FFFFFF',
  },
});

export { resolveHeaderCTA };
export default HeaderCTA;
